/**
 * Connection Service
 *
 * Service for managing connections in the node editor.
 * Handles fetching available connections, resolving connection references,
 * and managing connection state for workflow nodes.
 */

import type {
  ConnectionReference,
  ConnectionReferences,
  ConnectionMapping,
  AvailableConnection,
  ConnectionStatus,
  ReferenceKey,
  NodeId,
  ApiHubAuthentication,
} from '@/types/connectionReference';
import type { ConnectionsConfiguration, Connection } from '@/types/logicApps';
import {
  isAgentConnectionId,
  isManagedApiConnector,
  isServiceProviderConnector,
  toShortManagedApiId,
  MANAGED_MODEL_REFERENCE_KEY,
  MANAGED_MODEL_PROVIDER_ID,
  BACKEND_AGENT_CONNECTION_TYPES,
} from '@/utils/connections';
import { walkAllActions } from '@/utils/workflow';
import { isTemplateExpression } from './tokenParsingService';
// =============================================================================
// TYPES
// =============================================================================

/**
 * Options for fetching available connections
 */
export interface FetchConnectionsOptions {
  /** Azure subscription ID */
  subscriptionId: string;
  /** Resource group name */
  resourceGroup: string;
  /** Logic App name */
  appName: string;
  /** Bearer token for authentication */
  accessToken: string;
  /** Filter by connector/API ID */
  connectorId?: string;
}

/**
 * Connection resolution result
 */
export interface ResolvedConnection {
  /** Reference key used in the workflow */
  referenceKey: ReferenceKey;
  /** The actual connection reference */
  reference: ConnectionReference;
  /** Display name for the connection */
  displayName: string;
  /** Connection status */
  status: ConnectionStatus;
}

/**
 * Result of getting node connection
 */
export interface NodeConnectionResult {
  /** Whether the node has a connection */
  hasConnection: boolean;
  /** Reference key if exists */
  referenceKey: ReferenceKey | null;
  /** Resolved connection if exists */
  connection: ResolvedConnection | null;
  /** Whether a connection is required for the node */
  isRequired: boolean;
}

// =============================================================================
// CONNECTION RESOLUTION
// =============================================================================

/**
 * Get all connection references from a ConnectionsConfiguration
 * Handles managed API, service provider, and agent connections
 */
export function extractConnectionReferences(
  config: ConnectionsConfiguration | null | undefined
): ConnectionReferences {
  const references: ConnectionReferences = {};

  if (!config) return references;

  // Extract managed API connections
  if (config.managedApiConnections) {
    for (const [key, conn] of Object.entries(config.managedApiConnections)) {
      const ref: ConnectionReference = {
        api: { id: toShortManagedApiId(conn.api.id) },
        connection: conn.connection,
        // Prefer the explicit displayName when present (set by the portal
        // on creation, or by external tools that author connections.json).
        // Falls back to the key, which may be opaque for connections
        // created outside the portal (e.g. `apiconnection-0576`).
        connectionName: conn.displayName ?? key,
        connectionRuntimeUrl: conn.connectionRuntimeUrl,
        authentication: conn.authentication,
      };
      if (conn.connectionProperties && Object.keys(conn.connectionProperties).length > 0) {
        ref.connectionProperties = conn.connectionProperties;
      }
      references[key] = ref;
    }
  }

  // Extract service provider connections
  if (config.serviceProviderConnections) {
    for (const [key, conn] of Object.entries(config.serviceProviderConnections)) {
      const ref: ConnectionReference = {
        api: { id: conn.serviceProvider?.id ?? '' },
        connection: { id: key },
        connectionName: conn.displayName ?? key,
      };
      // Carry parameterValues + parameterSetName onto connectionProperties.
      // The dynamicInvoke payload builder reads `_parameterSetName` (the portal's
      // internal marker) from connectionProperties and lifts it back to
      // `parameterSetName` at the connection envelope. Without this, runtime
      // dynamic-list/schema calls fail because the engine can't pick the right
      // auth parameter set (issue #371 regression).
      const props: Record<string, unknown> = { ...(conn.parameterValues ?? {}) };
      if (typeof conn.parameterSetName === 'string' && conn.parameterSetName.length > 0) {
        props._parameterSetName = conn.parameterSetName;
      }
      if (Object.keys(props).length > 0) {
        ref.connectionProperties = props;
      }
      references[key] = ref;
    }
  }

  // Extract agent connections (Azure OpenAI, etc.)
  if (config.agentConnections) {
    for (const [key, conn] of Object.entries(config.agentConnections)) {
      // Agent connections use modelProvider.id or resourceId as the API identifier
      const apiId = conn.modelProvider?.id ?? conn.resourceId ?? `/agentConnections/${key}`;
      const ref: ConnectionReference = {
        api: { id: apiId },
        connection: { id: key },
        connectionName: conn.displayName ?? key,
      };
      // Store connection properties including endpoint and resourceId
      const connectionProps: Record<string, unknown> = {
        ...conn.parameterValues,
      };
      if (conn.modelConfiguration) {
        connectionProps.modelConfiguration = conn.modelConfiguration;
      }
      if (conn.endpoint) {
        connectionProps.endpoint = conn.endpoint;
      }
      if (conn.resourceId) {
        connectionProps.resourceId = conn.resourceId;
      }
      if (conn.type) {
        connectionProps.type = conn.type;
      }
      if (Object.keys(connectionProps).length > 0) {
        ref.connectionProperties = connectionProps;
      }
      if (conn.authentication) {
        ref.authentication = conn.authentication;
      }
      references[key] = ref;
    }
  }

  // Extract hosted agent connections (Foundry GHCP hosted agents)
  if (config.hostedAgentConnections) {
    for (const [key, conn] of Object.entries(config.hostedAgentConnections)) {
      const ref: ConnectionReference = {
        api: { id: `/hostedAgentConnections/${key}` },
        connection: { id: conn.connectionId ?? key },
        connectionName: conn.displayName ?? key,
      };
      const connectionProps: Record<string, unknown> = {};
      if (conn.configuration) {
        connectionProps.configuration = conn.configuration;
      }
      if (conn.endpoints) {
        connectionProps.endpoints = conn.endpoints;
      }
      if (conn.status) {
        connectionProps.status = conn.status;
      }
      if (Object.keys(connectionProps).length > 0) {
        ref.connectionProperties = connectionProps;
      }
      references[key] = ref;
    }
  }

  // Extract MCP connections from the serialized `agentMcpConnections` bucket.
  if (config.agentMcpConnections) {
    for (const [key, conn] of Object.entries(config.agentMcpConnections)) {
      const ref: ConnectionReference = {
        // `/mcpConnections/` is a portal-internal URI discriminator; it is
        // never serialized back to the runtime (the bucket is agentMcpConnections).
        api: { id: `/mcpConnections/${key}` },
        connection: { id: key },
        connectionName: conn.displayName ?? key,
      };
      const connectionProps: Record<string, unknown> = {};
      if (conn.mcpServerUrl) {
        connectionProps.endpoint = conn.mcpServerUrl;
      }
      if (conn.parameterValues) {
        connectionProps.parameterValues = conn.parameterValues;
      }
      if (Object.keys(connectionProps).length > 0) {
        ref.connectionProperties = connectionProps;
      }
      if (conn.authentication && typeof (conn.authentication as { type?: unknown }).type === 'string') {
        ref.authentication = conn.authentication as unknown as ApiHubAuthentication;
      }
      references[key] = ref;
    }
  }

  return references;
}

/**
 * Resolve a reference key to its full connection
 */
export function resolveConnectionReference(
  referenceKey: ReferenceKey,
  references: ConnectionReferences
): ResolvedConnection | null {
  const reference = references[referenceKey];
  if (!reference) return null;

  return {
    referenceKey,
    reference,
    displayName: reference.connectionName ?? referenceKey,
    status: 'connected' as ConnectionStatus,
  };
}

function hasMeaningfulConfigValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulConfigValue);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasMeaningfulConfigValue);
  return false;
}

/**
 * Whether a connection reference is fully usable for a real run.
 * Template placeholders often create reference shells with empty IDs / config,
 * which should not be treated as connected.
 */
export function isConnectionReferenceComplete(
  reference: ConnectionReference | null | undefined
): boolean {
  if (!reference?.api?.id || reference.api.id.trim().length === 0) {
    return false;
  }

  if (isManagedApiConnector(reference.api.id)) {
    return !!reference.connection?.id?.trim();
  }

  if (isServiceProviderConnector(reference.api.id)) {
    return hasMeaningfulConfigValue(reference.connectionProperties)
      || !!reference.authentication?.type?.trim();
  }

  if (isAgentConnectionId(reference.api.id)) {
    // `connection.id` is always derived from the storage key by
    // extractConnectionReferences (no `connection` field exists on
    // `AgentConnection`), so it cannot be used as a signal of configuration.
    // A real configured agent connection always has connectionProperties
    // (endpoint / modelConfiguration / parameterValues) or authentication;
    // template placeholders have neither.
    return hasMeaningfulConfigValue(reference.connectionProperties)
      || !!reference.authentication?.type?.trim();
  }

  if (reference.api.id.startsWith('/mcpConnections/')) {
    return hasMeaningfulConfigValue(reference.connectionProperties)
      || !!reference.connection?.id?.trim();
  }

  return !!reference.connection?.id?.trim()
    || hasMeaningfulConfigValue(reference.connectionProperties)
    || !!reference.authentication?.type?.trim();
}

/**
 * Whether a raw `ServiceProviderConnection` entry from connections.json carries
 * enough information to actually connect at runtime. Template-apply writes
 * placeholder rows with only `serviceProvider.id` + `displayName`; those are
 * never usable and should be hidden from the Connections list so the user
 * doesn't think a fake row is real.
 */
export function isServiceProviderConnectionUsable(
  conn: { parameterValues?: Record<string, unknown> | undefined; parameterSetName?: string | undefined } | null | undefined
): boolean {
  if (!conn) return false;
  return hasMeaningfulConfigValue(conn.parameterValues)
    || !!conn.parameterSetName?.trim();
}

/**
 * Whether a raw `AgentConnection` entry from connections.json carries enough
 * information to actually connect at runtime. The template-apply placeholder
 * has only `modelProvider.id` + `displayName`; real connections always have
 * at least one of endpoint / modelConfiguration / resourceId / parameterValues
 * / authentication.
 *
 * Note: Microsoft-managed refs are intentionally NOT persisted to
 * connections.json (see `connectionStore.exportToConnectionsConfig` and the
 * design-pivot note in `managedModelReference.ts`), so this
 * function is only ever called on BYOM-shaped entries — no Microsoft-managed branch needed.
 */
export function isAgentConnectionUsable(
  conn: {
    endpoint?: string | undefined;
    resourceId?: string | undefined;
    modelConfiguration?: Record<string, unknown> | undefined;
    parameterValues?: Record<string, unknown> | undefined;
    authentication?: { type?: string | undefined } | undefined;
  } | null | undefined
): boolean {
  if (!conn) return false;
  return !!conn.endpoint?.trim()
    || !!conn.resourceId?.trim()
    || hasMeaningfulConfigValue(conn.modelConfiguration)
    || hasMeaningfulConfigValue(conn.parameterValues)
    || !!conn.authentication?.type?.trim();
}

/**
 * Whether a raw `AgentConnection` entry has the required fields and a
 * runtime-supported literal type.
 * `AgentConnection` (extending `ManagedAgentConnection`) marks `type`,
 * `endpoint`, and `authentication` as Required.Always — an entry missing ANY
 * of them makes the whole connections(-draft).json unparseable
 * (EdgeConnectionsDataProvider → 400 WorkflowConnectionsInvalidConnectionValue),
 * blocking draft runs for every workflow in the app.
 *
 * Stricter than `isAgentConnectionUsable` (which answers "does this look
 * configured?" for UI purposes): a partially-configured entry — e.g. endpoint
 * only — is usable-looking but still unparseable, and must not be persisted.
 * Type expressions must be preserved: the runtime evaluates them before
 * deserialization, so their resolved values cannot be checked here.
 */
export function isAgentConnectionRuntimeParseable(
  conn: {
    type?: string | undefined;
    endpoint?: string | undefined;
    authentication?: { type?: string | undefined } | undefined;
  } | null | undefined
): boolean {
  if (!conn || typeof conn.type !== 'string') return false;
  const type = conn.type.trim().toLowerCase();
  return (BACKEND_AGENT_CONNECTION_TYPES.some((runtimeType) => runtimeType.toLowerCase() === type)
      || (!conn.type.startsWith('@@') && isTemplateExpression(conn.type)))
    && typeof conn.endpoint === 'string'
    && !!conn.authentication?.type?.trim();
}

/**
 * Get the connection for a specific node
 */
export function getNodeConnectionInfo(
  nodeId: NodeId,
  mapping: ConnectionMapping,
  references: ConnectionReferences,
  isRequired: boolean = false
): NodeConnectionResult {
  const referenceKey = mapping[nodeId] ?? null;

  if (!referenceKey) {
    return {
      hasConnection: false,
      referenceKey: null,
      connection: null,
      isRequired,
    };
  }

  const connection = resolveConnectionReference(referenceKey, references);

  return {
    hasConnection: !!connection,
    referenceKey,
    connection,
    isRequired,
  };
}

// =============================================================================
// CONNECTION MAPPING EXTRACTION
// =============================================================================

/**
 * Extract connection mapping from workflow definition
 * Maps node IDs to their connection reference keys
 */
export function extractConnectionMapping(
  workflow: { definition?: { triggers?: Record<string, unknown>; actions?: Record<string, unknown> } } | null
): ConnectionMapping {
  const mapping: ConnectionMapping = {};

  if (!workflow?.definition) return mapping;

  // Extract from triggers
  if (workflow.definition.triggers) {
    for (const [name, trigger] of Object.entries(workflow.definition.triggers)) {
      const refKey = extractReferenceKeyFromOperation(trigger as Record<string, unknown>);
      if (refKey) {
        mapping[`trigger-${name}`] = refKey;
      }
    }
  }

  // Extract from actions — recurse into nested scopes (If/Switch/Foreach/Until)
  // and agent tools so connectors inside conditional/loop branches or agent
  // tools are mapped too. The walker hands back the canvas node id directly
  // (e.g. `action-Parent/Child` for nested actions, `tool-Agent-SendEmail`
  // for agent tools), so we don't need to know the shape.
  if (workflow.definition.actions) {
    walkAllActions(workflow.definition.actions, (_names, action, _scopedName, nodeId) => {
      const refKey = extractReferenceKeyFromOperation(action);
      if (refKey) {
        mapping[nodeId] = refKey;
      }
    });
  }

  return mapping;
}

/**
 * Extract reference key from an operation (action or trigger)
 * Handles three connection patterns:
 * 1. API Connections: inputs.host.connection.referenceName
 * 2. Service Providers: inputs.serviceProviderConfiguration.connectionName
 * 3. Agent Connections: inputs.modelConfigurations.model1.referenceName
 *    (plus a Microsoft-managed fallback: agentModelType === 'MicrosoftManaged'
 *     resolves to the synthetic MANAGED_MODEL_REFERENCE_KEY when no explicit
 *     referenceName is present)
 */
export function extractReferenceKeyFromOperation(operation: Record<string, unknown>): ReferenceKey | null {
  const inputs = operation.inputs as Record<string, unknown> | undefined;
  if (!inputs) return null;

  // Check inputs.host.connection.referenceName (API connections)
  if (inputs.host) {
    const host = inputs.host as Record<string, unknown>;
    if (host.connection) {
      const conn = host.connection as Record<string, unknown>;
      if (typeof conn.referenceName === 'string') {
        return conn.referenceName;
      }
    }
  }

  // Check serviceProviderConfiguration.connectionName (service providers)
  if (inputs.serviceProviderConfiguration) {
    const spConfig = inputs.serviceProviderConfiguration as Record<string, unknown>;
    if (typeof spConfig.connectionName === 'string') {
      return spConfig.connectionName;
    }
  }

  // Check inputs.host.function.connectionName (Function connections)
  if (inputs.host) {
    const host = inputs.host as Record<string, unknown>;
    if (host.function) {
      const fn = host.function as Record<string, unknown>;
      if (typeof fn.connectionName === 'string') {
        return fn.connectionName;
      }
    }
  }

  // Check inputs.mcpConfiguration.connectionName (MCP connections)
  if (inputs.mcpConfiguration) {
    const mcpConfig = inputs.mcpConfiguration as Record<string, unknown>;
    if (typeof mcpConfig.connectionName === 'string') {
      return mcpConfig.connectionName;
    }
  }

  // Check modelConfigurations.model1.referenceName (Agent connections)
  if (inputs.modelConfigurations) {
    const modelConfigs = inputs.modelConfigurations as Record<string, unknown>;
    // Agent connections typically use model1 as the primary model configuration
    const model1 = modelConfigs.model1 as Record<string, unknown> | undefined;
    if (model1 && typeof model1.referenceName === 'string') {
      return model1.referenceName;
    }
    // Also check for other model configurations (model2, etc.)
    for (const [, modelConfig] of Object.entries(modelConfigs)) {
      if (modelConfig && typeof modelConfig === 'object') {
        const config = modelConfig as Record<string, unknown>;
        if (typeof config.referenceName === 'string') {
          return config.referenceName;
        }
      }
    }
  }

  // Microsoft-managed agent fallback: workflows authored outside the
  // portal (SDK, CLI, hand-written) only need to set `agentModelType` — they
  // don't have to emit a phantom `modelConfigurations.model1.referenceName`
  // pointing at the synthetic `managedAiConnection` ref, because the runtime
  // dispatches on `agentModelType === 'MicrosoftManaged'` alone. Return
  // the synthetic ref key here so mapping / picker / badges all light up.
  const parameters = inputs.parameters as Record<string, unknown> | undefined;
  const agentModelType = (parameters?.agentModelType ?? inputs.agentModelType) as string | undefined;
  if (agentModelType === MANAGED_MODEL_PROVIDER_ID) {
    return MANAGED_MODEL_REFERENCE_KEY;
  }

  return null;
}

// =============================================================================
// AVAILABLE CONNECTIONS
// =============================================================================

/**
 * Convert Connection (from API) to AvailableConnection format
 */
export function toAvailableConnection(conn: Connection): AvailableConnection {
  return {
    id: conn.connectionId,
    name: conn.name,
    connectorId: conn.apiId,
    status: 'connected' as ConnectionStatus,
  };
}

/**
 * Filter available connections by connector ID
 */
export function filterConnectionsByConnector(
  connections: AvailableConnection[],
  connectorId: string
): AvailableConnection[] {
  if (!connectorId) return connections;

  return connections.filter((conn) => {
    // Handle both full resource IDs and short names
    return (
      conn.connectorId === connectorId ||
      conn.connectorId.toLowerCase().includes(connectorId.toLowerCase())
    );
  });
}

/**
 * Get available connections for a specific connector from the current workflow
 */
export function getAvailableConnectionsForConnector(
  references: ConnectionReferences,
  connectorId: string
): AvailableConnection[] {
  const connections: AvailableConnection[] = [];

  for (const [key, ref] of Object.entries(references)) {
    // Check if API ID matches
    if (ref.api.id.toLowerCase().includes(connectorId.toLowerCase())) {
      connections.push({
        id: ref.connection.id,
        name: ref.connectionName ?? key,
        connectorId: ref.api.id,
        status: 'connected' as ConnectionStatus,
      });
    }
  }

  return connections;
}

// =============================================================================
// REFERENCE KEY GENERATION
// =============================================================================

/**
 * Generate a unique reference key for a new connection
 */
export function generateUniqueReferenceKey(
  existingKeys: string[],
  connectorName: string
): string {
  // Extract the connector short name (e.g., "office365" from "/providers/.../managedApis/office365")
  const baseName = connectorName
    .split('/')
    .pop()
    ?.replace(/[^a-zA-Z0-9]/g, '') || 'connection';

  let key = baseName;
  let counter = 1;

  while (existingKeys.includes(key)) {
    key = `${baseName}_${counter}`;
    counter++;
  }

  return key;
}

// =============================================================================
// CONNECTION VALIDATION
// =============================================================================

/**
 * Validate that a connection exists and is properly configured.
 * Delegates to `isConnectionReferenceComplete` which handles per-type rules
 * (managed API needs connection.id; agent/SP connections need connectionProperties).
 */
export function validateConnection(
  referenceKey: ReferenceKey,
  references: ConnectionReferences
): { isValid: boolean; error?: string } {
  const reference = references[referenceKey];

  if (!reference) {
    return {
      isValid: false,
      error: `Connection reference "${referenceKey}" not found`,
    };
  }

  // For managed API connections, provide specific guidance on what's missing.
  const apiId = reference.api?.id ?? '';
  if (!isAgentConnectionId(apiId) && !apiId.startsWith('/serviceProviders/') && !apiId.startsWith('/mcpConnections/')) {
    if (!apiId) {
      return { isValid: false, error: `Connection "${referenceKey}" has no API configured` };
    }
    if (!reference.connection?.id?.trim()) {
      return { isValid: false, error: `Connection "${referenceKey}" has no connection ID` };
    }
  }

  if (!isConnectionReferenceComplete(reference)) {
    return {
      isValid: false,
      error: `Connection "${referenceKey}" is not fully configured`,
    };
  }

  return { isValid: true };
}

/**
 * Check if a node requires a connection based on its operation type
 */
export function doesOperationRequireConnection(
  operationType: string
): boolean {
  // Types that typically require connections
  const connectionTypes = [
    'ApiConnection',
    'ApiConnectionWebhook',
    'ApiConnectionNotification',
    'ServiceProvider',
    'Agent', // Agent actions require model connections (Azure OpenAI, etc.)
  ];

  return connectionTypes.includes(operationType);
}
